import { Box, Flex, Heading, Text } from '@ghq-abi/design-system';
import { useTranslate } from '@tolgee/react';

type ListCardHeaderProps = {
  title: string;
  status: string;
  rawStatus?: string;
  ticketNumber?: string;
  employeeName?: string;
  businessUnit?: string;
  zone?: string;
  category?: string;
  subcategory?: string;
  formType?: 'conflict-of-interest' | 'gifts-and-donations' | 'other-compliance-matters' | 'employee-movement' | 'unknown';
  createdDate?: string;
};

export function ListCardHeader({
  title,
  status,
  rawStatus,
  ticketNumber,
  employeeName,
  businessUnit,
  zone,
  category,
  subcategory,
  formType,
  createdDate,
}: ListCardHeaderProps) {
  const { t } = useTranslate();

  const labelCss = { fontSize: '$sm', textTransform: 'uppercase' as const };
  const valueCss = { fontSize: '$xs', color: '$gray700' };

  return (
    <Box
      css={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
        columnGap: '$3',
        rowGap: '$3',
        alignItems: 'start',
        width: '$full',
        overflow: 'hidden',
        wordBreak: 'break-word',

        '& > *': {
          minHeight: '48px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          minWidth: 0
        },
      }}
    >
      <Flex direction="column">
        <Heading as="strong" size="sm" css={{ fontSize: '$sm' }}>
          {t(title)}
        </Heading>

        {category && (
          <Text css={{ ...valueCss, marginTop: '$1' }}>
            {t(category)}
          </Text>
        )}

        {subcategory && formType !== 'conflict-of-interest' && (
          <Text css={{ ...valueCss, color: '$gray500' }}>
            {t(subcategory)}
          </Text>
        )}
      </Flex>

      <Flex direction="column">
        <Heading as="strong" size="sm" css={labelCss}>
          {t('myTickets.ticketId')}
        </Heading>
        <Text css={{ ...valueCss, wordBreak: 'break-all' }}>{ticketNumber}</Text>
      </Flex>

      <Flex direction="column">
        <Heading as="strong" size="sm" css={labelCss}>
          {t('myTickets.subcategory')}
        </Heading>
        <Text css={valueCss}>
          {subcategory ? t(subcategory) : '—'}
        </Text>
      </Flex>

      <Flex direction="column">
        <Heading as="strong" size="sm" css={labelCss}>
          {t('my_tickets.list.employee_name')}
        </Heading>
        <Text css={valueCss}>{employeeName}</Text>
      </Flex>

      <Flex direction="column">
        <Heading as="strong" size="sm" css={labelCss}>
          {t('my_tickets.list.business_unit')}
        </Heading>
        <Text css={valueCss}>{businessUnit}</Text>
      </Flex>

      <Flex direction="column">
        <Heading as="strong" size="sm" css={labelCss}>
          {t('common.zone')}
        </Heading>
        <Text css={valueCss}>{zone}</Text>
      </Flex>

      <Flex direction="column">
        <Heading as="strong" size="sm" css={labelCss}>
          {t('common.status')}
        </Heading>
        <Text css={valueCss}>
          {t(`my_tickets.status.${status}`)}
        </Text>
      </Flex>

      <Flex direction="column">
        <Heading as="strong" size="sm" css={labelCss}>
          {t('myTickets.createdDate')}
        </Heading>
        <Text css={valueCss}>{createdDate}</Text>
      </Flex>
    </Box>
  );
}